import { ArticleError, ArticleHref, ArticleInfo, ArticleMain, sideInfo, sideItem } from "./shared.js";
import { initThemeToggle } from "./theme.js";
import { initFontToggle } from "./fontchoice.js";
import { initSocialCompact, initShare } from "./social.js";
import { formatDateDMY, fdate, makeDatesNoWrap } from "./utils.js";
// import { debug } from "console";

// Declare Google CSE types and search handler
declare global {
    interface Window {
        google: any;
        handleSearch: (event: Event) => void;
    }
}

if (typeof fetch === "undefined") {
    alert("This application requires a modern browser that supports the Fetch API.");
    throw new Error("Fetch API not supported.");
}

export const isV2 = true;   // To enable finding dependencies

export const PrimarySite = "frankston.com";
export const KeywordSite = "rmf.vc";
export const PrimarySiteKeys = ["writing", "public", "home", "bio", "biov2", "furtherreadings"];

// Aliases for compatibility
export const rmfvcSite = KeywordSite;
export const FrankstonSite = PrimarySite;
export const FrankstonComKeys = PrimarySiteKeys;

const descElement = document.querySelector('.header-desc') as HTMLDivElement;
const titleElement = document.querySelector('.header-title') as HTMLDivElement;
const placeElement = document.querySelector('.header-placeholder') as HTMLDivElement;
const noteElement = document.querySelector('.dbg-note') as HTMLDivElement;
const xEssayElement = document.querySelector('.xessay') as HTMLDivElement;
const errNote = document.querySelector('.err-note') as HTMLDivElement;
// const sideBar = document.querySelector('.side-bar') as HTMLDivElement;
const headerItem = document.getElementById('headeritem');

function initDebugHandler() {
    // 2026-10-13 Claude Code (Opus 5.5), at Bob's direction: two levels now.
    //   Alt+D            -> debug mode (body.debug-mode): the info panel and debug-only controls
    //                       such as the essay-font button. Alt+D again turns it all off.
    //   outline button   -> grid outlines (body.debug-grid, the existing outline CSS), a sub-toggle
    //   in the panel        inside debug mode.
    // Alt+D used to only reveal a "🐛 Debug" button that then had to be clicked to get outlines;
    // that button (and its viewport-size label, which the panel already shows) is gone.
    const body = document.body;

    document.addEventListener('keydown', (e) => {
        if (e.altKey && e.key.toLowerCase() === 'd') {
            e.preventDefault();
            const on = body.classList.toggle('debug-mode');
            if (!on)
                body.classList.remove('debug-grid');   // Leaving debug mode clears the outlines too
            updateDebugInfo();
        }
    });

    // Debug info panel: a persistent outline button plus a text area rewritten on each update
    const debugInfo = document.createElement('div');
    debugInfo.className = 'debug-info';
    debugInfo.id = 'debug-info';
    debugInfo.style.display = 'none';

    const outlineButton = document.createElement('button');
    outlineButton.className = 'debug-outline-btn';
    // Outline image: a dashed frame around three boxes, i.e. the grid areas being outlined
    outlineButton.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6">
        <rect x="2" y="2" width="20" height="20" rx="2" stroke-dasharray="3 2"/>
        <rect x="5" y="5" width="14" height="4"/><rect x="5" y="11" width="5" height="8"/><rect x="12" y="11" width="7" height="8"/></svg>`;
    outlineButton.addEventListener('click', (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        body.classList.toggle('debug-grid');
        updateDebugInfo();
    });

    const debugText = document.createElement('div');
    debugInfo.append(outlineButton, debugText);
    document.body.appendChild(debugInfo);

    function updateDebugInfo() {
        const isDebugging = body.classList.contains('debug-mode');
        debugInfo.style.display = isDebugging ? 'block' : 'none';

        const outlines = body.classList.contains('debug-grid');
        outlineButton.classList.toggle('active', outlines);
        outlineButton.title = outlines ? 'Grid outlines are on\nClick to hide them' : 'Grid outlines are off\nClick to show them';

        if (isDebugging) {
            const width = window.innerWidth;
            const height = window.innerHeight;

            // Get breakpoint info from CSS custom properties
            const computedStyle = getComputedStyle(document.documentElement);
            const breakpoint = computedStyle.getPropertyValue('--breakpoint-name').trim().replace(/['"]/g, '');
            const gridColumns = computedStyle.getPropertyValue('--grid-columns-display').trim().replace(/['"]/g, '');

            debugText.innerHTML = `
                <strong>Debug Mode</strong><br>
                Viewport: ${width} × ${height}px<br>
                Breakpoint: ${breakpoint}<br>
                Grid: ${gridColumns}<br>
                Theme: ${document.documentElement.className || 'auto'}
            `;
        }
    }

    // Update debug info on resize
    window.addEventListener('resize', updateDebugInfo);
    updateDebugInfo();
}

function initNavigationHandling() {
    // Handle general navigation events (non-social)
    // Add any navigation-specific handlers here if needed
    
    // Handle search toggle button
    const searchToggle = document.getElementById('search-toggle-btn');
    const searchContainer = document.getElementById('search-container');
    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    
    if (searchToggle && searchContainer) {
        searchToggle.addEventListener('click', () => {
            searchContainer.classList.toggle('hide');
            
            // Focus on search input when showing
            if (!searchContainer.classList.contains('hide')) {
                searchInput?.focus();
            }
        });
        
        // Optional: Hide search when clicking outside
        document.addEventListener('click', (event) => {
            if (!searchToggle.contains(event.target as Node) && 
                !searchContainer.contains(event.target as Node)) {
                searchContainer.classList.add('hide');
            }
        });
    }
}

// Global search handler function
window.handleSearch = function(event: Event) {
    event.preventDefault();
    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    const query = searchInput.value.trim();
    
    if (query) {
        const searchUrl = `https://www.google.com/search?udm=14&q=${encodeURIComponent(query)}+site:frankston.com+OR+site:rmf.vc`;
        window.open(searchUrl, '_blank', 'noopener,noreferrer');
    }
}

document.addEventListener("DOMContentLoaded", async function () {
    // Viewport tracking
    const updateViewportSize = () => {
        document.documentElement.style.setProperty('--viewport-width', window.innerWidth + 'px');
        document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
    };
    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);

    initDebugHandler();
    initThemeToggle();
    initFontToggle();   // 2026-10-11 Claude Code: experimental essay font, button shown in debug mode only
    initNavigationHandling();

    // Debugging hack
    if (this.location.hostname.startsWith('home.')) 
        (document.querySelector('.nav-compact-header') as HTMLDivElement).style.backgroundColor = 'crimson';

    errNote.innerHTML = ''; // Clear

    // Use event delegation for fetchable buttons and in-app navigation
    document.addEventListener('click', function (ev) {
        const target = ev.target as HTMLElement;
        const button = target.closest('button.fetchable') as HTMLButtonElement;

        if (button) {
            ev.preventDefault();
            ev.stopPropagation();

            const fetchKey = button.dataset.fetchKey || button.textContent?.trim();
            if (fetchKey) {
                fetchByKey(fetchKey);
            }
            return;
        }

        // Intercept sidebar topic links to fetch in-app without a full page reload (same-origin only)
        const sideLink = target.closest('a.side-ref') as HTMLAnchorElement;
        if (sideLink && sideLink.href) {
            try {
                const url = new URL(sideLink.href, location.origin);
                // Only intercept in-app if on the exact same origin (e.g. home.rmf.vc -> home.rmf.vc)
                // Cross-domain links (e.g. frankston.com -> rmf.vc) should follow normal navigation
                if (url.origin === location.origin) {
                    const keyMatch = /^\/([-a-z0-9_]+)($|\?|\#)/i.exec(url.pathname);
                    if (keyMatch && keyMatch[1]) {
                        ev.preventDefault();
                        ev.stopPropagation();
                        const nextKey = keyMatch[1];
                        history.pushState(null, '', url.pathname + url.search + url.hash);
                        fetchByKey(nextKey);
                        return;
                    }
                }
            } catch (e) {
                // Fallback to default browser navigation
            }
        }

        // Intercept hash-only links to prevent unwanted navigation
        const link = target.closest('a') as HTMLAnchorElement;
        if (link && link.href) {
            const href = link.getAttribute('href');
            // Check if it's a hash-only link (starts with #)
            if (href && href.startsWith('#') && href.length > 1) {
                ev.preventDefault();
                const anchorName = href.substring(1); // Remove the #
                scrollToAnchorWithName(anchorName);
                // Optionally update URL hash without triggering navigation
                history.replaceState(null, '', location.pathname + location.search + href);
            }
        }
    });

    // Handle browser back/forward history navigation
    window.addEventListener('popstate', () => {
        let key = /^https?:\/\/[^\/]+\/([-\#_a-z0-9]+)($|\?|\#)/i.exec(location.href);
        let params = getQueryParameters();
        if ('column' in params) {
            fetchByColumn((params as any).column);
            return;
        }
        fetchByKey(key ? key[1] : 'home');
    });

    if (noteElement) {
        noteElement.textContent = location.href;
    }

    fixYear(document);
    // Initialize social handling
    initSocialCompact();
    initShare();        // Browser share sheet
    // 2026-10-10 Claude Code (Opus 5.5): legacy per-network share row removed at Bob's direction
    await createSideBar();
    // Strip hash fragment before extracting key to avoid treating anchors as page keys
    // const urlWithoutHash = location.href.split('#')[0];
    // let key = /^https?:\/\/[^\/]+\/([-\#_a-z0-9]+)($|\?)/i.exec(urlWithoutHash);
    let key = /^https?:\/\/[^\/]+\/([-\#_a-z0-9]+)($|\?|\#)/i.exec(location.href);

    let params = getQueryParameters();
    if ('column' in params) {
        fetchByColumn((params as any).column);   // Fetch by column
        return;
    }
    fetchByKey(key ? key[1] : 'home');
});


function fixYear(el: Document) { 
    try {
        fixItem(el, 'year', new Date().getFullYear().toString());
    }
    catch (e: any) {
        alert("Report bug " + e);   // While debugging
    }
}

function fixItem(el: Document, value: string, content: string) {
    try {
        let spanElements = el.querySelectorAll("span[rmf\\:value='" + value + "']");
        for (let i = 0; i < spanElements.length; i++) {
            spanElements[i].textContent = content;
        }
    }
    catch (e: any) {
        alert("Report bug " + e);   // For debugging
    }
}

async function createSideBar() {
    try {
        const response = await fetch("/fetch/sidebar" + location.search);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.text();

        // Validate the response is JSON
        if (!result.trim().startsWith('{')) {
            throw new Error(`Invalid JSON response: ${result.substring(0, 100)}`);
        }

        const sideInfo = JSON.parse(result) as sideInfo;
        let sideDivs = document.getElementById("side-items") as HTMLDivElement;

        if (!sideDivs) {
            throw new Error("side-items element not found");
        }

        // console.log("Creating sidebar with", sideInfo.sideItems.length, "items");
        fixItem(document, "version", sideInfo.version);
        sideInfo.sideItems.forEach((si: sideItem) => {
            try {
                let sitemplate = document.getElementById("side-item-template") as HTMLTemplateElement;
                let siclone = sitemplate.content.cloneNode(true) as HTMLElement;
                let sd = siclone.firstElementChild;
                let nd = sd.querySelector(".side-date");
                nd.textContent = fdate(si.created);
                if (si.updated) nd.innerHTML += " (<i>" + fdate(si.updated) + "</i>)";
                let nt = sd.querySelector(".side-title") as HTMLDivElement;
                nt.textContent = si.title;
                let nr = sd.querySelector(".side-ref") as HTMLAnchorElement;
                if (si.isDraft) {
                    sd.classList.add('v2draft', 'side-draft')   // Add a draft watermark    
                }
                if (si.feature) {
                    sd.classList.add('toc-primary');
                } else {
                    sd.classList.add('toc-secondary');
                }
                nr.href = si.keyref;
                sideDivs.appendChild(sd);
            }
            catch (e: any) {
                alert(e + " " + JSON.stringify(si));
            }
        });
    }
    catch (e: any) {
        alert(e);
    }
}

function keymapper(key: string) {
    key = key.toLowerCase();
    if (isV2) {
        // TODO: Remove for the production version
        switch (key) {
            // case "bio": key = "biov2"; break;
        }
    }
    return key;
}

async function fetchByKey(key: string) {
    let keyed = key.split('#'); // Try to support #
    let keylc = keymapper(keyed[0]) || 'home';
    let url = '/bykey/' + keylc + location.search;
    // The key regex stops at '?', so a URL like /page?x=1#anchor loses its hash — recover it from location.hash
    let anchor = keyed[1] || (location.hash ? location.hash.substring(1) : undefined);
    // Keyword references belong to KeywordSite
    const isPrimarySiteKey = PrimarySiteKeys.includes(keylc);
    const targetHost = isPrimarySiteKey ? undefined : KeywordSite;
    await fetcher(url, anchor, targetHost);
}

async function fetchByColumn(column: string) { // Should we force this to KeywordSite?
    await fetcher('/column/' + column, null, KeywordSite);
}

/**
 * Fetches data from a given URL and processes the response to update the DOM or redirect the user.
 *
 * @param url - The URL to fetch data from. Can be a relative or absolute URL.
 * @param hash - (Optional) A hash string to append to the URL. Non-alphanumeric characters are removed.
 * @param host - (Optional) A host string to override the current host. If provided, the URL is adjusted to use this host.
 *
 * The function performs the following:
 * - Appends a sanitized hash to the URL if provided.
 * - Adjusts the URL to use a specified host if provided.
 * - Sends a request using `fetch` and processes the response.
 * - Updates various DOM elements based on the response data, such as `xEssayElement`, `titleElement`, and `descElement`.
 * - Handles errors by displaying them in the DOM or logging them to the console.
 * - Redirects the user if the response contains a `href` property.
 * - Updates the page title and other metadata based on the response.
 * - Handles draft-specific behavior, such as displaying a watermark and draft information.
 * - Scrolls to an anchor if a hash is provided in the response.
 *
 * @throws Will alert the user if an exception occurs during the fetch process.
 */
async function fetcher(url: string, hash?: string, host?: string) {
    try {
        // let fetchOptions: RequestInit = {
        //     // Add headers or other options as needed
        // };
        if (hash) {
            hash = hash.replace(/[^a-z0-9_-]/gi, '');
            url = url.includes('?') ? url + '&hash=' + hash : url + '?hash=' + hash;
        }

        if (host) {
            // Adjust host to preserve environment prefix (home., beta1., etc.) and port
            if (url[0] != '/') url = "/" + url;    // Just to be extra safe
            let parts = location.hostname.split('.');
            if (parts.length > 2) {
                let prefix = parts.slice(0, -2).join('.');
                if (prefix) {
                    host = prefix + "." + host;
                }
            }
            const port = location.port ? ':' + location.port : '';
            url = location.protocol + "//" + host + port + url;
            // console.log(`Host[${host}] URL[${url}]`);
        }

        const response = await fetch(url); // , fetchOptionsbeta1
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} ${response.statusText}  ${url}`);
        }

        const result = await response.text();

        // // Check if the response is a vCard
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/vcard')) {
            // Handle vCard response
            const vCardData = result;   // Naming for self-documentation
            // Trigger download or handle vCard appropriately
            const blob = new Blob([vCardData], { type: 'text/vcard' });
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = 'contact.vcf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(downloadUrl);
            // ALso add the vcard to the body as a pre
            const pre = document.createElement('pre');
            pre.textContent = vCardData;
            if (xEssayElement) {
                xEssayElement.innerHTML = ''; // Clear existing content
                xEssayElement.appendChild(pre);
            }   
            document.title = "vCard Downloaded";
            // titleElement.inerterHTML = "<h1 class='title'>vCard Downloaded</h1>";
            // const headerItem = document.getElementById('headeritem');
            headerItem.style.display = 'none';
            return;
        }

        const data = JSON.parse(result) as ArticleMain;    /// Unless proven aotherse;

        if ('baseURL' in data)
            setBaseURL(data.baseURL);   // Set base URL early because page may rely on it
        if (xEssayElement) {
            try {
                anchorCache = null;  // Anchors from the previous document are stale
                // Use createContextualFragment to preserve script execution
                // Old version simply assigned datta.html to xEssayElement.innerHTML
                xEssayElement.innerHTML = ''; // Clear existing content
                const range = document.createRange();
                range.selectNodeContents(xEssayElement);
                const fragment = range.createContextualFragment(data.html);
                xEssayElement.appendChild(fragment);
            }
            catch (e: any) {
                xEssayElement.textContent = String(e);
                xEssayElement.style.backgroundColor = 'pink';
            debugger;
            }
        }

        // const headerItem = document.getElementById('headeritem');
        headerItem.style.display = "";  // Default

        // Reset grid layout
        const gridContainer = document.querySelector('.grid-container') as HTMLElement;
        if (gridContainer) {
            gridContainer.classList.remove('fullwidth-layout');  // Reset to CSS default
        }

        // Remove watermark
        const waterMark = document.querySelector('.draftWatermarkDiv') as HTMLElement;
        if (waterMark) {
            waterMark.style.display = 'none';
        }
        titleElement.classList.remove('header-draft');

        if ('errhtml' in data) {
            const edata = data as ArticleError;
            errNote.innerHTML = edata.errhtml;
            errNote.style.display = 'block';
            titleElement.innerHTML = "<h1 class='title'>Error</h1>";
            placeElement.innerHTML = '';     // Clear placeholder
            return;
        }
        errNote.style.display = 'none';

        if ('href' in data) {
            const hdata = data as ArticleHref;
            window.open(hdata.href, '_self');
            return;
        }

        updateMetaTags(data);
        if (titleElement) {
            placeElement.innerHTML = '';     // Clear placeholder
            if (data.header)
                titleElement.innerHTML = data.header;
            else {
                ``
                let hdr = document.createElement('h1');
                hdr.className = 'title';
                hdr.textContent = data.title;
                titleElement.appendChild(hdr);
            }
        }
        if (descElement) {
            descElement.innerHTML = data.desc || '';
            descElement.style.display = descElement.textContent == "" ? 'none' : 'block';
            const docInfoElement = document.querySelector("docInfo");
            if (docInfoElement?.getAttribute("hideheader") == "true") {
                headerItem.style.display = 'none';
            }
            if (docInfoElement?.getAttribute("fullwidth") == "true") {
                const gridContainer = document.querySelector('.grid-container') as HTMLElement;
                if (gridContainer) {
                    gridContainer.classList.add('fullwidth-layout');
                }
            }
        }
        if (data.isDraft) {
            if (waterMark) {
                waterMark.style.display = 'block';
            }
            titleElement.classList.add('header-draft');
            const dspan = document.createElement("span");
            dspan.classList.add("draft-span");
            dspan.innerText = "DRAFT";
            titleElement.appendChild(dspan);
        }
        // Assuming setBaseURL is defined elsewhere
        if (data.created) {
            let dc = document.querySelector('.header-created');
            dc.textContent = formatDateDMY(data.created);
            if (data.updated)
                dc.innerHTML += '<br/><i>updated:</i></br>' + formatDateDMY(data.updated);
            if (data.isDraft) {
                const docInfoElement = document.querySelector("docInfo");
                if (docInfoElement) {
                    const createdValue = docInfoElement.getAttribute("created");
                    const countValue = docInfoElement.getAttribute("count");
                    if (createdValue) {
                        const draftLabel = countValue && countValue !== 'null' ? `DRAFT #${countValue}` : 'DRAFT';
                        dc.innerHTML += '<div id="draftinfo" class="draft-info"><b>' + draftLabel + '</b><br/>' + createdValue + '</div>';
                    }
                }
            }
        }
        hash = hash || data.hash;   // Passing it back from the server after we redirect
        if (hash) {
            setTimeout(() => { scrollToAnchorWithName(hash); }, 0); // Scroll to anchor
        }
    } catch (ex: any) {
        console.error(ex);
        if (xEssayElement) {
            xEssayElement.textContent = String(ex);
            xEssayElement.style.backgroundColor = 'pink';
        }
        debugger;
    }
}

/**
 * Implement the equivalent of #hash
 */

// Lowercased anchor name (id or a[name]) → element; built lazily, cleared when new content loads
let anchorCache: Map<string, HTMLElement> | null = null;

function buildAnchorCache(): Map<string, HTMLElement> {
    const map = new Map<string, HTMLElement>();
    document.querySelectorAll<HTMLElement>('[id], a[name]').forEach((el: HTMLElement) => {
        const anchorName = el.id || el.getAttribute('name');
        if (!anchorName) return;
        const key = anchorName.toLowerCase();
        if (!map.has(key)) map.set(key, el);   // First occurrence wins
    });
    return map;
}

// Mimic hash behavior using 'name' or 'id' attribute on any element
function scrollToAnchorWithName(name: string) {
    try {
        // First try to find by ID (modern standard), exact match
        const elementById = document.getElementById(name);
        if (elementById) {
            return elementById.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        // Fallback: case-insensitive lookup via cached map of lowercased ids/names
        if (!anchorCache) anchorCache = buildAnchorCache();
        const element = anchorCache.get(name.toLowerCase());
        if (element) {
            return element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }
    catch (e: any) {
        // Ignore errors
    }
}

// queryParameters.js

/**
 * Get query parameters from a URL as an object
 * @param {string} [url] - The URL to get query parameters from. Default is window.location.href.
 * @returns {Object} An object containing the query parameters.
 */
function getQueryParameters(url?: string) {
    let params = {};
    url = url || window.location.href;
    let queryString = url.split('#')[0].split("?")[1];
    if (!queryString) return {};

    let queryArray = queryString.split("&");

    for (let i = 0; i < queryArray.length; i++) {
        let query = queryArray[i];
        let keyValue = query.split("=");
        let key = decodeURIComponent(keyValue[0]);
        let value = decodeURIComponent(keyValue[1] || "");
        (params as any)[key] = value;
    }

    return params;
}


function setBaseURL(baseURL: string, target?: string) {
    if (!baseURL) return;   // Not sent??
    target = target || '_self';
    let baseTag = document.createElement("base");
    baseTag.href = baseURL;
    baseTag.target = target;
    document.head.appendChild(baseTag);
    // console.log(baseTag.outerHTML);
}

/**
 * Dynamically updates document title, Open Graph, and Twitter metadata on client-side navigation
 * Ensures browser extensions, web clippers, and native share sheets see the current article's metadata.
 */
function updateMetaTags(data: ArticleMain) {
    if (!data) return;
    const pageTitle = data.title ? `${data.title} - Bob Frankston` : "Bob Frankston";
    document.title = pageTitle;

    function setMeta(attr: string, key: string, content: string) {
        if (!content) return;
        let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
        if (!el) {
            el = document.createElement('meta');
            el.setAttribute(attr, key);
            document.head.appendChild(el);
        }
        el.setAttribute('content', content);
    }

    function setLink(rel: string, href: string) {
        if (!href) return;
        let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
        if (!el) {
            el = document.createElement('link');
            el.setAttribute('rel', rel);
            document.head.appendChild(el);
        }
        el.setAttribute('href', href);
    }

    const plainDesc = data.desc ? data.desc.replace(/<[^>]*>/g, '').trim() : '';
    const currentUrl = location.href.split('#')[0];

    setMeta('name', 'description', plainDesc);
    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', plainDesc);
    setMeta('property', 'og:url', currentUrl);
    setMeta('name', 'twitter:title', pageTitle);
    setMeta('name', 'twitter:description', plainDesc);
    setLink('canonical', currentUrl);
}
